import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { createPortal } from 'react-dom';
import { Layers } from 'react-feather';
import { verifyAccess, truncateData, getModuleCrudPermissions } from '../../../../services';
import {
  EmbeddedProvidersTypes,
  EmbeddedLlmDetailsProps,
  EmbeddedModelsTypes,
} from '../embedded.props';
import { getEmbeddedProviders, getEmbeddedModels } from '../services/embedded-llm';
import { usePageHeader } from '../../user';
import ErrorFallback from '../../../../components/error-fallback';
import { FilterSection, CustomTable, Tooltip, SelectionActionBar } from '../../../../components';
import type { ColumnDef } from '../../../../components';
import Logout from '../../../../auth/protected/session-expire-modal';
import { tableFunctionsProps } from '../../user/pages/users/user.props';
import AddEditEmBeddedLLM from './add-edit-embedded-llm';
import AddEmbeddedProvider from './add-embedded-provider';
import DeleteEmbeddedLLM from './delete-embedded-llm';
import makeToast from '../../../../components/toaster';

type EmbeddingModalType = 'none' | 'add-edit' | 'delete' | 'add-provider';

const emptyEmbedding: EmbeddedLlmDetailsProps = {
  id: 0,
  provider_id: 1,
  name: '',
  description: '',
  input_token_pricing: undefined,
};

function LLMConfigModule() {
  const navigate = useNavigate();
  const [logout, setLogout] = useState<boolean>(false);
  const [showError, setShowError] = useState(false);
  const [modalType, setModalType] = useState<EmbeddingModalType>('none');
  const [dataToEdit, setDataToEdit] = useState<EmbeddedLlmDetailsProps>(emptyEmbedding);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableFunctions, setTableFunctions] = useState<tableFunctionsProps>({
    limit: Number(process.env.REACT_APP_ROWS_PER_PAGE) || 10,
    offset: 0,
    search: '',
    sort_col: 'name',
    sort_order: 'desc',
  });
  const [dataToDelete, setDataToDelete] = useState<Array<number>>([]);
  const [providers, setProviders] = useState<Array<EmbeddedProvidersTypes>>([]);
  const [models, setModels] = useState<Array<EmbeddedModelsTypes>>([]);
  const [dataUpdated, setDataUpdated] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [embeddingCount, setEmbeddingCount] = useState<number>(0);

  useEffect(() => {
    const hasAccess = verifyAccess('embedding', '/embedding');
    if (!hasAccess) {
      navigate('/unauthorized');
    }
  }, [navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getEmbeddedProviders()
        .then((res) => {
          if (res.status === 200) {
            setProviders(res.data?.data || []);
          } else if (res.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          setShowError(true);
          makeToast('error', 'Something went wrong!');
        });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [dataUpdated]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getEmbeddedModels(tableFunctions)
        .then((res) => {
          if (res.status === 200) {
            setModels(res.data?.data.models || []);
            setEmbeddingCount(res.data?.data.total_count || 0);
            setLoading(false);
          } else if (res.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Something went wrong!');
          setShowError(true);
        });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [dataUpdated, tableFunctions]);

  const filteredModels = useMemo(() => {
    if (!searchQuery.trim()) return models;
    const q = searchQuery.toLowerCase();
    return models.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) ||
        m.provider_name?.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q),
    );
  }, [models, searchQuery]);

  const handleDeleteCheckBoxClick = (id: number) => {
    setDataToDelete((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleClearSelection = () => setDataToDelete([]);

  const handleMultiDataDelete = () => setModalType('delete');

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const ids = filteredModels.map((m) => m.id);
      setDataToDelete((prev) => [...new Set([...prev, ...ids])]);
    } else {
      const idsToRemove = new Set(filteredModels.map((m) => m.id));
      setDataToDelete((prev) => prev.filter((id) => !idsToRemove.has(id)));
    }
  };

  const embeddingColumns = useMemo((): ColumnDef<EmbeddedModelsTypes>[] => {
    return [
      {
        key: 'model',
        header: 'Model Name',
        width: 'w-[38%]',
        headerIcon: (
          <div className="w-10 h-10 rounded-full bg-[#e0f1f8] flex items-center justify-center flex-shrink-0">
            <Layers className="w-4 h-4 text-[#0A8BC7]" />
          </div>
        ),
        render: (row: EmbeddedModelsTypes) => {
          const modelName = row.name ?? '';
          return (
            <div className="flex items-center gap-3 min-w-0 ml-2">
              <input
                type="checkbox"
                className="border-[#101828] border-[2px] w-[15px] h-[15px]"
                onChange={() => handleDeleteCheckBoxClick(row.id)}
                checked={dataToDelete.includes(row.id)}
              />
              <div className="w-10 h-10 rounded-full bg-[#e0f1f8] flex items-center justify-center flex-shrink-0">
                <Layers className="w-4 h-4 text-[#0A8BC7]" />
              </div>
              <div className="min-w-0 flex-1">
                <Tooltip title={modelName}>
                  <p className="font-medium text-gray-900 truncate">
                    {truncateData(modelName, 30)}
                  </p>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        key: 'provider',
        header: 'Provider',
        width: 'w-[15%]',
        render: (row: EmbeddedModelsTypes) => {
          const providerName = row.provider_name || '—';
          return (
            <Tooltip title={providerName}>
              <span className="block truncate">{truncateData(providerName, 20)}</span>
            </Tooltip>
          );
        },
      },
      {
        key: 'input_pricing',
        header: 'Input Price',
        width: 'w-[15%]',
        align: 'center',
        render: (row: EmbeddedModelsTypes) => (
          <span className="font-medium text-[#6A6A6A]">
            ${row.input_token_pricing?.toFixed(2) ?? '0'}
          </span>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        width: 'w-[30%]',
        render: (row: EmbeddedModelsTypes) => {
          const desc = row.description || 'No description';
          return (
            <Tooltip title={desc}>
              <span className="block truncate text-gray-600">{truncateData(desc, 40)}</span>
            </Tooltip>
          );
        },
      },
    ];
  }, [dataToDelete]);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEditEmbedding = (model: EmbeddedModelsTypes) => {
    setDataToEdit(model as EmbeddedLlmDetailsProps);
    setModalType('add-edit');
  };

  const handleDeleteEmbedding = (model: EmbeddedModelsTypes) => {
    setDataToDelete([model.id]);
    setModalType('delete');
  };

  const handleAddEmbedding = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    setDataToEdit(emptyEmbedding);
    setModalType('add-edit');
  };

  const handleAddProvider = () => {
    setModalType('add-provider');
  };

  const totalPages = Math.ceil(embeddingCount / tableFunctions.limit) || 1;
  const currentPage = Math.floor(tableFunctions.offset / tableFunctions.limit) + 1;

  const goToPage = (page: number) => {
    const offset = (page - 1) * tableFunctions.limit;
    setTableFunctions((prev) => ({ ...prev, offset }));
    setLoading(true);
  };

  const hasActiveFilters = searchQuery.trim() !== '';

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setTableFunctions((prev) => ({ ...prev, search: '', offset: 0 }));
    setLoading(true);
  };

  const {
    hasCreate: hasCreatePermission,
    hasUpdate: hasEditPermission,
    hasDelete: hasDeletePermission,
  } = getModuleCrudPermissions('embedding');

  const headerLeftContent = useMemo(
    () => (
      <div className="flex flex-wrap items-center justify-between gap-4 w-full">
        <div>
          <h1 className="text-xl font-bold text-gray-900">All Embeddings</h1>
          <p className="text-gray-500 text-sm mt-1">Manage embedding models and providers</p>
        </div>
        <div className="flex gap-2">
          {hasCreatePermission && (
            <button
              type="button"
              onClick={handleAddProvider}
              className="px-4 py-2 text-sm font-medium text-[#0A8BC7] border-1 border-[#0A8BC7] hover:bg-[#0A8BC7]/10 rounded-lg transition-colors"
            >
              Add Provider
            </button>
          )}
          {hasCreatePermission && (
            <button
              type="button"
              onClick={handleAddEmbedding}
              className="px-4 py-2 text-sm font-medium text-[#F48B00] border-1 border-[#F48B00] hover:bg-[#F48B00]/10 rounded-lg transition-colors"
            >
              Add Embedding
            </button>
          )}
        </div>
      </div>
    ),
    [hasCreatePermission],
  );

  usePageHeader({ className: 'flex-col gap-y-[20px] justify-between', headerLeftContent });

  if (showError) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <ErrorFallback />
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Filters */}
        <FilterSection
          searchValue={searchQuery}
          onSearchChange={(v) => {
            setSearchQuery(v);
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(() => {
              setTableFunctions((prev) => ({ ...prev, search: v, offset: 0 }));
              setLoading(true);
              searchTimeoutRef.current = null;
            }, 300);
          }}
          searchPlaceholder="Search by model name, provider, or description..."
          filters={[]}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearAllFilters}
        />

        <SelectionActionBar
          selectedCount={dataToDelete.length}
          onClear={handleClearSelection}
          onDelete={handleMultiDataDelete}
        />

        {/* Embeddings Table */}
        <CustomTable<EmbeddedModelsTypes>
          selectionConfig={{
            selectedIds: dataToDelete,
            getRowId: (row: EmbeddedModelsTypes) => row.id,
            onSelectAll: handleSelectAll,
          }}
          columns={embeddingColumns}
          data={filteredModels}
          rowKey="id"
          loading={loading}
          totalCount={embeddingCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
          title="Embeddings"
          emptyMessage="No embeddings found matching your filters."
          loadingMessage="Loading embeddings..."
          actions={{
            onEdit: (row: EmbeddedModelsTypes) => hasEditPermission && handleEditEmbedding(row),
            onDelete: (row: EmbeddedModelsTypes) =>
              hasDeletePermission && handleDeleteEmbedding(row),
            canEdit: () => hasEditPermission,
            canDelete: () => hasDeletePermission,
          }}
        />
      </div>

      <AddEditEmBeddedLLM
        open={modalType === 'add-edit'}
        setOpen={(open) => setModalType(open ? 'add-edit' : 'none')}
        dataToEdit={dataToEdit}
        setDataUpdated={setDataUpdated}
        providers={providers}
      />
      <DeleteEmbeddedLLM
        open={modalType === 'delete'}
        setDeleteLLMModalOpen={(open) => setModalType(open ? 'delete' : 'none')}
        dataToDelete={dataToDelete}
        setDataUpdated={setDataUpdated}
        setDataToDelete={setDataToDelete}
      />
      <AddEmbeddedProvider
        openProviderModal={modalType === 'add-provider'}
        setOpenProviderModal={(open) => setModalType(open ? 'add-provider' : 'none')}
        setDataUpdated={setDataUpdated}
      />
      {logout && createPortal(<Logout />, document.body)}
    </>
  );
}

export default LLMConfigModule;
